import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { ColDef, ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { customTheme } from "../../../../../config/theme/MuiTheme";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { ContextOperation } from "../../../context/OperationContext";
import useOperationTranslation from "../../../hooks/useOperationTranslation";
import useSnackbar from "../../../hooks/useSnackbar";
import useToggle from "../../../hooks/useToggle";
import OperationTypes from "../../../types";
import TableDynamicActions from "../../TableDynamicActions";
import EmptyStateGrid from "../../common/EmptyStateGrid";
import PhotoGallery, { TypeImages } from "../../common/PhotoGallery";
import { CreateDamageFormTypes } from "../../damageFormList/Types";
import DamageFormService from "../../damageFormList/service/DamageFormService";
import { DamageTypes } from "../Types";
import DamagePriceService from "../service/damagePrice.action";
import Dialogs from "./dialogs";
import AgGridOperationTable from "../../common/AgGridOperationTable";

const Damages = () => {
    const t = useOperationTranslation();
    const { tenant, setLoading } = useContext(ContextOperation);

    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as DamageTypes.FormByCode;
    
    const snackbar = useSnackbar();
    
    const tabs = [
        "form_damages",
        "vehicle_damages"
    ];
    const [selectedTab, setSelectedTab] = useState("form_damages");

    const [isOpenCreateDamage, toggleCreateDamage] = useToggle();
    const [isOpenEditDamage, toggleEditDamage] = useToggle();
    const [isOpenDeleteConfirmation, toggleDeleteConfirmation] = useToggle();
    const [isOpenPhotoGallery, togglePhotoGallery] = useToggle();
    
    const [parts, setParts] = useState<OperationTypes.VehiclePart[]>([]);
    const [severities, setSeverities] = useState<string[]>([]);
    const [selectedDamage, setSelectedDamage] = useState<OperationTypes.Damage | undefined>();
    const [photos, setPhotos] = useState<TypeImages[]>([]);
    const [datasourceNewDamages, setDatasourceNewDamages] = useState<OperationTypes.Damage[]>([]);
    const [datasourceExistingDamages, setDatasourceExistingDamages] = useState<OperationTypes.Damage[]>([]);
    const [damageDate, setDamageDate] = useState<string>("");

    useEffect(() => {
	
		setLoading(true);
	
        // get datasources for damages
        DamagePriceService.get.getAllDamagesByFormId(tenant, locationState.id).then(
            response => {
                setDatasourceNewDamages(response);

                setDamageDate(response[0].damageDate || "");
            }
        );

        DamagePriceService.get.existingDamages(tenant, locationState.plate).then(
            response => setDatasourceExistingDamages(response),
        );
        
        // get parts & severities
        DamageFormService.create.get.partPosition(tenant).then(
            response => setParts(response),
        );

        DamageFormService.create.get.severity().then(
            response => setSeverities(response),
        );
		
		setLoading(false);
		
    }, []);

    // EDIT DAMAGE
    const onOpenEditDamage = (damage?: OperationTypes.Damage) => {
        setSelectedDamage(damage);
        toggleEditDamage();
    }
    const onCloseEditDamage = () => {
        setSelectedDamage(undefined);
        toggleEditDamage();
    }

    // DELETE DAMAGE
    const onOpenDeleteConfirmation = (damage?: OperationTypes.Damage) => {
        setSelectedDamage(damage);
        toggleDeleteConfirmation();
    }
    const onCloseDeleteConfirmation = () => {
        setSelectedDamage(undefined);
        toggleDeleteConfirmation();
    }

    const onDeleteDamage = () => {
        if (selectedDamage?.id) {
            DamagePriceService.delete.damage(selectedDamage?.id).then(
                (response) => {
                    setDatasourceNewDamages(datasourceNewDamages.filter((damage) => damage.id !== selectedDamage?.id));
                    toggleDeleteConfirmation();
                }).catch((error: any) => {
                    setLoading(false);
                    snackbar.show("error", t("damageList.damageCreation.message.deleteDamageErrorMessage"));
                }
            );
        }
    }

    // CLOSE PHOTO GALLERY
    const onClosePhotoGallery = () => {
        togglePhotoGallery();
        setPhotos([]);
    }

    const severityCellRenderer = (params: ICellRendererParams<CreateDamageFormTypes.IRespCreateDamage>) => {
        const color = params.data?.severity === "SOSTITUZIONE" ? "info" :
            params.data?.severity === "LIEVE" ? "success" :
            params.data?.severity === "MEDIO" ? "warning" :
            params.data?.severity === "GRAVE" ? "error" : 
            "default";
        return (
            <Mui.Chip
                label={params.data?.severity}
                variant="filled"
                color={color}
            />
        )
    }

    const columnsNewDamages: ColDef[] = [
        {
            headerName: t("damageList.damageCreation.damages.fields.part"),
            field: "partId",
            flex: 1,        
            valueFormatter: (params: ValueFormatterParams<OperationTypes.Damage>) => {
                return parts.find((part) => part.part.id === params.data?.part.id && part.position.id === params.data?.position.id)?.label || "";
            }
        },
        {
            headerName: t("damageList.damageCreation.damages.fields.severity"),
            field: "severity",
            flex: 1,
            cellRenderer: severityCellRenderer,
        },
        {
            headerName: "",
            field: "_actions",
            maxWidth: 100,
            cellRenderer: (params: ICellRendererParams<OperationTypes.Damage>) => {
                return (
                    <TableDynamicActions moreColor={customTheme.palette.accent.main}>
                        {params.data?.temporary && (
                            <Mui.Tooltip
                                title={t("damageList.damageCreation.button.edit")}
                                placement={"left"}
                            >
                                <Mui.Button
                                    variant="text"
                                    size="icon"
                                    onClick={() => onOpenEditDamage(params.data)}
                                >
                                    <MuiIcons.Edit />
                                </Mui.Button>
                            </Mui.Tooltip>
                        )}

                        {params.data?.temporary && (
                            <Mui.Tooltip
                            title={t("damageList.damageCreation.button.delete")}
                            placement={"left"}
                            >
                                <Mui.Button
                                    variant="text"
                                    size="icon"
                                    onClick={() => onOpenDeleteConfirmation(params.data)}
                                >
                                    <MuiIcons.Delete />
                                </Mui.Button>
                            </Mui.Tooltip>
                        )}

                        <Mui.Tooltip
                            title={t("damageList.damageCreation.button.images")}
                            placement={"left"}
                        >
                            <Mui.Button
                                variant="text"
                                size="icon"
                                disabled={!params.data?.firstSnapshotId && !params.data?.secondSnapshotId}
                                onClick={() => openPhotos(params.data)}
                            >
                                <MuiIcons.Image />
                            </Mui.Button>
                        </Mui.Tooltip>
                    </TableDynamicActions>
                )
            }
        }
    ];

    const columnsExistingDamages: ColDef[] = [
        {
            headerName:  t("damageList.damageCreation.damages.fields.part"),
            field: "part",
            flex: 1,
            valueFormatter: (params: ValueFormatterParams<OperationTypes.Damage>) => {
                return parts.find((part) => part.part.id === params.data?.part.id && part.position.id === params.data?.position.id)?.label || "";
			}
        },
        {
            headerName:  t("damageList.damageCreation.damages.fields.severity"),
            field: "severity",
            flex: 1,
            cellRenderer: severityCellRenderer,
        },
        {
            headerName: "",
            field: "_actions",
            maxWidth: 70,
            cellRenderer: (params: ICellRendererParams<OperationTypes.Damage>) => {
                return (
                    <TableDynamicActions moreColor={customTheme.palette.accent.main}>
                        <Mui.Tooltip
                            title={t("damageList.damageCreation.button.images")}
                            placement={"left"}
                        >
                            <Mui.Button
                                variant="text"
                                size="icon"
                                disabled={!params.data?.firstSnapshotId && !params.data?.secondSnapshotId}
                                onClick={() => openPhotos(params.data)}
                            >
                                <MuiIcons.Image />
                            </Mui.Button>
                        </Mui.Tooltip>
                    </TableDynamicActions>
                )
            }
        }
    ]

    const openPhotos = (damage?: OperationTypes.Damage) => {
        if (!damage) return;

        setLoading(true);
        DamageFormService.create.get.photos(damage.id.toString()).then(
            response => {
                if (response.length == 1) {
                    Promise.all([
                        DamageFormService.create.get.photoBlob(response[0].id.toString()),
                    ]).then(
                        response => {
                            setPhotos(response.map((res) => ({ base64: res })));
                            setLoading(false);
                            togglePhotoGallery();
                        }
                    )
                } else if (response.length >= 2) {
                    Promise.all([
                        DamageFormService.create.get.photoBlob(response[0].id.toString()),
                        DamageFormService.create.get.photoBlob(response[1].id.toString()),
                    ]).then(
                        response => {
                            setPhotos(response.map((res) => ({ base64: res })));
                            setLoading(false);
                            togglePhotoGallery();
                        }
                    )
                }
            },
            error => {
                setLoading(false);
            }
        )
    }

    const onCreateDamage = (damage: OperationTypes.Damage) => {
        setDatasourceNewDamages([ ...datasourceNewDamages, damage ]);
    }

    const onFormConfirmation = () => {
        const data: CreateDamageFormTypes.IReqAssignDamage[] = [
            {
                formId: locationState.id,
                damageIdList: datasourceNewDamages.map(element => element.id),
            }
        ]

        setLoading(true);
        DamagePriceService.put.assignDamage(tenant, true, data).then(
            () => {
                navigate(`/operation/damages/forms/details/${locationState.id}`);
                setLoading(false);
            },            
            (error) => {

                setLoading(false);
                if (error.response.status == 409) {
                    snackbar.show("error", t("damageList.damageCreation.message.409errorDamagesCreation"));
                }
                else {
                    snackbar.show("error", t("damageList.damageCreation.message.errorDamagesCreation"));
                }

            }
        )
    }

    const availableParts = useMemo(() => {
        return parts.filter((part) => 
            !datasourceNewDamages.find((damage) => damage.part.id === part.part.id && damage.position.id === part.position.id)
        );
    }, [parts, datasourceNewDamages]);

    const isCreateDamagesDisabled = useMemo(() => {
        return datasourceNewDamages.find((element) => element.temporary === true) ? false : true;
    }, [datasourceNewDamages]);

    return(
        <>
            <Mui.Card style={{ margin: "12px" }}>
                <Mui.Box component={"div"}>
                    <Mui.Tabs
                        value={selectedTab}
                        onChange={(_event, value) => setSelectedTab(value)}
                    >
                        <Mui.Tab
                            label={
                                <div>
                                    <Mui.Typography fontWeight="bold">
                                        {t("damageList.damageCreation.damages.tab1Card")}
                                    </Mui.Typography>
                                </div>
                            }
                            value={tabs[0]}
                        ></Mui.Tab>
                        <Mui.Tab
                            label={
                                <div>
                                    <Mui.Typography fontWeight="bold">
                                        {t("damageList.damageCreation.damages.tab2Card")}
                                    </Mui.Typography>
                                </div>
                            }
                            value={tabs[1]}
                        ></Mui.Tab>
                    </Mui.Tabs>
                </Mui.Box>
                    
                <Mui.CardContent>
                    {selectedTab === tabs[0] && (
                        <Mui.Box>                                                        
                            <Mui.Grid container mb="10px" justifyContent="flex-end">                                                                           
                                <Mui.Grid item xs="auto" ml="5px" mr="5px">
                                    <Mui.Button
                                        variant="text"
                                        size="icon"
                                        onClick={toggleCreateDamage}
                                    >
                                        <MuiIcons.Add />
                                    </Mui.Button>
                                </Mui.Grid>        
                            </Mui.Grid>

                            <AgGridOperationTable
                                rowData={datasourceNewDamages}
                                columnDefs={columnsNewDamages}
								rowModelType="clientSide"
                                totalElements={datasourceNewDamages.length}
                                 emptyState={{
                                    title: t("damageList.damageCreation.damages.emptyState.title"),
                                    content: t("damageList.damageCreation.damages.emptyState.content1")
                                }}
                            /> 
                          
                        </Mui.Box>
                    )}

                    {selectedTab === tabs[1] && (
                        <Mui.Box>
                            <AgGridOperationTable
                                columnDefs={columnsExistingDamages}
                                rowData={datasourceExistingDamages}
								rowModelType="clientSide"
                                totalElements={datasourceExistingDamages.length}
                                emptyState={{
                                    title: t("damageList.damageCreation.damages.emptyState.title"),
                                    content: t("damageList.damageCreation.damages.emptyState.content1")
                                }}
                            />

                        </Mui.Box>
                    )}
                </Mui.CardContent>
                
                <Mui.CardActions>
                    <Mui.Grid container spacing={2} justifyContent="end">
                        <Mui.Grid item xs={12} md={"auto"}>
                            <Mui.Button 
                                variant="outlined" 
                                fullWidth 
                                size="medium"
                                onClick={() => navigate(-1)}
                            >
                                <Mui.Typography color={customTheme.palette.accent.main}>
                                    {t("damageList.damageCreation.button.back")} 
                                </Mui.Typography>
                            </Mui.Button>
                        </Mui.Grid>

                        <Mui.Grid item xs={12} md={"auto"}>
                            <Mui.Button 
                                variant="contained"
                                fullWidth 
                                type="submit" 
                                onClick={onFormConfirmation}
                                size="medium"
                                disabled={isCreateDamagesDisabled}
                            >
                                <Mui.Typography variant="h6" color={'accent'}>
                                    {t("damageList.damageCreation.button.createForm")} 
                                </Mui.Typography>
                            </Mui.Button>
                        </Mui.Grid>
                    </Mui.Grid>
                </Mui.CardActions>
            </Mui.Card>

            <Dialogs.CreateDamage
                open={isOpenCreateDamage}
                onClose={toggleCreateDamage}
                onCreateDamage={onCreateDamage}
                parts={availableParts}
                severities={severities}
                plate={locationState.plate}
            />

            <Dialogs.EditDamage
                open={isOpenEditDamage}
                onClose={onCloseEditDamage}
                parts={parts}
                severities={severities}
                damage={selectedDamage}
            />

            <Dialogs.DeleteConfirmation
                isOpen={isOpenDeleteConfirmation}
                onClose={onCloseDeleteConfirmation}
                parts={parts}
                damage={selectedDamage}
                onDelete={onDeleteDamage}
            />


            <PhotoGallery
                images={photos}
                onClose={onClosePhotoGallery}
                open={isOpenPhotoGallery}
            />
        </>
    )
};
export default Damages